/**
 * Empty State Component
 * Beautiful messages when no data is available
 */

import React from 'react';
import { Link } from 'react-router-dom';

interface EmptyStateProps {
  icon?: string;
  title: string;
  description?: string;
  actionLabel?: string;
  actionLink?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon = '📦',
  title,
  description,
  actionLabel,
  actionLink,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
      <div className="max-w-md mx-auto">
        <span className="text-6xl block mb-4">{icon}</span>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        {description && (
          <p className="text-gray-600 mb-6">{description}</p>
        )}
        
        {(actionLabel || secondaryActionLabel) && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {actionLabel && (
              actionLink ? (
                <Link
                  to={actionLink}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium"
                >
                  {actionLabel}
                </Link>
              ) : onAction ? (
                <button
                  onClick={onAction}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium"
                >
                  {actionLabel}
                </button>
              ) : null
            )}
            
            {secondaryActionLabel && onSecondaryAction && (
              <button
                onClick={onSecondaryAction}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium"
              >
                {secondaryActionLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmptyState;

// Pre-configured empty states for common scenarios
export const NoProductsFound: React.FC<{ onReset?: () => void }> = ({ onReset }) => (
  <EmptyState
    icon="🔍"
    title="No products found"
    description="Try adjusting your search or filters to find what you're looking for."
    actionLabel="Browse All Products"
    actionLink="/products"
    secondaryActionLabel={onReset ? "Clear Filters" : undefined}
    onSecondaryAction={onReset}
  />
);

export const NoOrdersYet: React.FC = () => (
  <EmptyState
    icon="📦"
    title="No orders yet"
    description="Start shopping to see your orders here."
    actionLabel="Browse Products"
    actionLink="/products"
  />
);

export const NoFarmersFound: React.FC = () => (
  <EmptyState
    icon="🌾"
    title="No farmers found"
    description="Check back later or adjust your search criteria."
    actionLabel="View All Farmers"
    actionLink="/farmers"
  />
);

export const NoUsersFound: React.FC<{ onCreate?: () => void }> = ({ onCreate }) => (
  <EmptyState
    icon="👥"
    title="No users found"
    description="Start by adding your first user to the platform."
    actionLabel="Add User"
    onAction={onCreate}
  />
);

export const ComingSoon: React.FC<{ feature: string }> = ({ feature }) => (
  <EmptyState
    icon="🚀"
    title={`${feature} Coming Soon`}
    description="We're working hard to bring you this feature. Stay tuned!"
    actionLabel="Back to Dashboard"
    actionLink="/admin"
  />
);

